/**
 * Stage Access Control Logic
 * Enforces the 6-step workflow with strict forward progression rules
 */

import type { CourseDraft, WorkflowStage } from '../types/creator-workflow';

export interface StageRequirement {
  stage: WorkflowStage;
  labelKey: string;
  icon: string;
  order: number; // Stage order: 1-6
  isAccessible: (draft: CourseDraft) => boolean;
  missingRequirements: (draft: CourseDraft) => string[];
}

/**
 * Define stage order for progression control
 * Note: audio_merging has been merged into audio_generation (Stage 3)
 * Note: subtitle_editing is handled by SubtitleEditorPage, not shown in wizard progress
 *
 * Workflow stages (6 total, matching Stage0-Stage5 files):
 * 0 - Material Input (Stage0MaterialInput)
 * 1 - AI Generation (Stage1Generation)
 * 2 - Vocabulary Analysis (Stage2Parsing)
 * 3 - Audio Generation (Stage3AudioGeneration)
 * 4 - Validation (Stage4Validation)
 * 5 - Publishing (Stage5Publishing)
 */
const STAGE_ORDER: Record<WorkflowStage, number> = {
  material_input: 0,
  subtitle_editing: 0,           // Same as material_input (handled by separate page)
  conversational_editing: 0.5,   // Between material_input and parsing (conversational mode)
  generation: 1,
  sentence_editing: 1.5,         // Between generation and parsing (text workflow)
  parsing: 2,
  audio_generation: 3,
  validation: 4,
  publishing: 5,
};

/**
 * Get stage order number
 */
export const getStageOrder = (stage: WorkflowStage): number => {
  return STAGE_ORDER[stage];
};

/**
 * Check if user can access a stage (no forward jumping allowed)
 * Can only access current stage or previous stages
 */
export const canAccessStage = (targetStage: WorkflowStage, draft: CourseDraft): boolean => {
  const currentOrder = getStageOrder(draft.current_stage);
  const targetOrder = getStageOrder(targetStage);

  // Can only access current or previous stages
  if (targetOrder > currentOrder) {
    return false;
  }

  // Check if stage has required prerequisites (mode-aware)
  const stages = getStageRequirements(draft);
  const requirement = stages.find(r => r.stage === targetStage);
  return requirement ? requirement.isAccessible(draft) : false;
};

/**
 * Check if a stage is accessible based on prerequisites
 */
export const checkStageAccess = (stage: WorkflowStage, draft: CourseDraft): boolean => {
  const stages = getStageRequirements(draft);
  const requirement = stages.find(r => r.stage === stage);
  return requirement ? requirement.isAccessible(draft) : false;
};

/**
 * Get missing requirements for a stage
 */
export const getMissingRequirements = (stage: WorkflowStage, draft: CourseDraft): string[] => {
  const stages = getStageRequirements(draft);
  const requirement = stages.find(r => r.stage === stage);
  return requirement ? requirement.missingRequirements(draft) : [];
};

/**
 * All possible stage requirement definitions.
 * getStageRequirements() selects the right subset based on draft mode.
 */

const STAGE_MATERIAL_INPUT: StageRequirement = {
  stage: 'material_input',
  labelKey: 'workflow.stages.materialInput',
  icon: '📁',
  order: 0,
  isAccessible: () => true,
  missingRequirements: () => [],
};

const STAGE_CONVERSATIONAL_EDITING: StageRequirement = {
  stage: 'conversational_editing',
  labelKey: 'workflow.stages.conversationalEditing',
  icon: '💬',
  order: 1,
  isAccessible: (draft) => {
    // Accessible after audio uploaded or text provided
    return !!(draft.uploaded_audio_url || draft.generated_text);
  },
  missingRequirements: (draft) => {
    const missing: string[] = [];
    if (!draft.uploaded_audio_url && !draft.generated_text) {
      missing.push('Upload audio or provide text first');
    }
    return missing;
  },
};

const STAGE_GENERATION: StageRequirement = {
  stage: 'generation',
  labelKey: 'workflow.stages.contentGeneration',
  icon: '🤖',
  order: 1,
  isAccessible: (draft) => !!(draft.cefr_level && draft.content_type),
  missingRequirements: (draft) => {
    const missing: string[] = [];
    if (!draft.cefr_level) missing.push('CEFR level not set');
    if (!draft.content_type) missing.push('Content type not set');
    return missing;
  },
};

const STAGE_PARSING: StageRequirement = {
  stage: 'parsing',
  labelKey: 'workflow.stages.vocabularyAnalysis',
  icon: '🔍',
  order: 2,
  isAccessible: (draft) => !!(draft.generated_text && draft.generated_text.length > 0),
  missingRequirements: (draft) => {
    const missing: string[] = [];
    if (!draft.generated_text || draft.generated_text.length === 0) {
      missing.push('Content not generated yet');
    }
    return missing;
  },
};

const STAGE_AUDIO_GENERATION: StageRequirement = {
  stage: 'audio_generation',
  labelKey: 'workflow.stages.audioGeneration',
  icon: '🎙️',
  order: 3,
  isAccessible: (draft) => !!(draft.sentences && draft.sentences.length > 0),
  missingRequirements: (draft) => {
    const missing: string[] = [];
    if (!draft.sentences || draft.sentences.length === 0) {
      missing.push('Sentences not analyzed yet');
    }
    return missing;
  },
};

const STAGE_VALIDATION: StageRequirement = {
  stage: 'validation',
  labelKey: 'workflow.stages.validation',
  icon: '✅',
  order: 4,
  isAccessible: (draft) => {
    const audioGen = draft.stage_progress?.audio_generation;
    const hasMergedAudio = audioGen?.merged_audio_url || audioGen?.segmented_audio_data?.merged_audio_url;
    const hasSubtitles = audioGen?.subtitle_url_en || audioGen?.segmented_audio_data?.subtitle_url_en;
    return !!(
      draft.generated_text &&
      draft.vocabulary &&
      draft.vocabulary.length > 0 &&
      hasMergedAudio &&
      hasSubtitles
    );
  },
  missingRequirements: (draft) => {
    const missing: string[] = [];
    if (!draft.generated_text) missing.push('Content not generated');
    if (!draft.vocabulary || draft.vocabulary.length === 0) missing.push('Vocabulary not analyzed');
    const audioGen = draft.stage_progress?.audio_generation;
    const hasMergedAudio = audioGen?.merged_audio_url || audioGen?.segmented_audio_data?.merged_audio_url;
    const hasSubtitles = audioGen?.subtitle_url_en || audioGen?.segmented_audio_data?.subtitle_url_en;
    if (!hasMergedAudio) missing.push('Audio not generated yet');
    if (!hasSubtitles) missing.push('Subtitles not generated yet');
    return missing;
  },
};

const STAGE_PUBLISHING: StageRequirement = {
  stage: 'publishing',
  labelKey: 'workflow.stages.publishing',
  icon: '🚀',
  order: 5,
  isAccessible: (draft) => {
    const validation = draft.stage_progress?.validation;
    const isValid = validation?.validation_result?.is_valid;
    const hasMinScore = (validation?.validation_result?.overall_score || 0) >= 70;
    return !!(isValid && hasMinScore);
  },
  missingRequirements: (draft) => {
    const missing: string[] = [];
    const validation = draft.stage_progress?.validation;
    if (!validation || !validation.completed) {
      missing.push('Validation not completed yet');
    } else {
      if (!validation.validation_result?.is_valid) missing.push('Content validation failed');
      const score = validation.validation_result?.overall_score || 0;
      if (score < 70) missing.push(`Content score too low (${score}/100, minimum 70 required)`);
    }
    return missing;
  },
};

/** Classic (text) workflow: material → generation → parsing → audio → validation → publishing */
const CLASSIC_STAGE_REQUIREMENTS: StageRequirement[] = [
  STAGE_MATERIAL_INPUT,
  STAGE_GENERATION,
  STAGE_PARSING,
  STAGE_AUDIO_GENERATION,
  STAGE_VALIDATION,
  STAGE_PUBLISHING,
];

/** Conversational workflow: material → conversational editing → parsing → audio → validation → publishing */
const CONVERSATIONAL_STAGE_REQUIREMENTS: StageRequirement[] = [
  STAGE_MATERIAL_INPUT,
  STAGE_CONVERSATIONAL_EDITING,
  STAGE_PARSING,
  STAGE_AUDIO_GENERATION,
  STAGE_VALIDATION,
  STAGE_PUBLISHING,
];

/**
 * Get stage requirements for a specific draft (mode-aware).
 * Falls back to classic if no draft provided.
 */
export const getStageRequirements = (draft?: CourseDraft | null): StageRequirement[] => {
  // conversational_mode defaults to true (null/undefined = on, only false = off)
  if (draft && draft.conversational_mode !== false) {
    return CONVERSATIONAL_STAGE_REQUIREMENTS;
  }
  return CLASSIC_STAGE_REQUIREMENTS;
};

/** Default export for backwards compatibility (classic flow) */
export const STAGE_REQUIREMENTS = CLASSIC_STAGE_REQUIREMENTS;

/**
 * Get the furthest accessible stage (based on prerequisites only)
 */
export const getFurthestAccessibleStage = (draft: CourseDraft): WorkflowStage => {
  const stages = getStageRequirements(draft);
  for (let i = stages.length - 1; i >= 0; i--) {
    if (stages[i].isAccessible(draft)) {
      return stages[i].stage;
    }
  }
  return 'material_input';
};

/**
 * Check if stage is locked (cannot be accessed)
 */
export const isStageLocked = (stage: WorkflowStage, draft: CourseDraft): boolean => {
  return !canAccessStage(stage, draft);
};

/**
 * Get lock reason for a stage
 */
export const getStageLockReason = (stage: WorkflowStage, draft: CourseDraft): string => {
  const currentOrder = getStageOrder(draft.current_stage);
  const targetOrder = getStageOrder(stage);

  if (targetOrder > currentOrder) {
    return `Complete Step ${currentOrder} (${draft.current_stage}) first`;
  }

  const missing = getMissingRequirements(stage, draft);
  if (missing.length > 0) {
    return missing[0];
  }

  return 'Stage not accessible';
};
